import {
  getApiGamesByGameIdActivities,
  getApiGamesByGameIdPlateAppearances,
  NextLink,
} from "@aura-frontend/wgs-client";
import { useQuery } from "@tanstack/react-query";

import { Activity, GameFullResponse, PlateAppearance } from "../types/gameFull";
import { SnapshotNext, toActivity, toPlateAppearance } from "./gameSnapshot";

/**
 * The game's full history. The /full snapshot holds only the newest 100
 * pitches and at bats, so a long game (or one left running untagged) has
 * older ones the page can't show or fill. This pages back through both lists
 * in the background, from where the snapshot's `next` links leave off. Most
 * games fit in the snapshot, so nothing is fetched.
 */

export type GameHistory = {
  activities: Activity[];
  plateAppearances: PlateAppearance[];
};

/** Loading until the older pages are merged into the game data. */
export type GameHistoryStatus = "loading" | "done" | "error";

const PAGE_LIMIT = 100;
/** A runaway cursor can't page forever: 50 pages is 5,000 pitches */
const MAX_PAGES = 50;

/** `next` is a link to the older page; its cursor is what to send back. */
export const cursorOf = (next: NextLink) =>
  next ? new URL(next, "http://placeholder").searchParams.get("cursor") : null;

/** Every page from `next` on (none when it's null). */
const fetchAllPages = async <T>(
  next: NextLink,
  fetchPage: (cursor: string) => Promise<{ items: T[]; next: NextLink }>,
) => {
  const items: T[] = [];
  let cursor = cursorOf(next);
  for (let page = 0; cursor && page < MAX_PAGES; page++) {
    const result = await fetchPage(cursor);
    items.push(...result.items);
    cursor = cursorOf(result.next);
  }
  return items;
};

const fetchGameHistory = async (
  gameId: string,
  next: SnapshotNext,
): Promise<GameHistory> => {
  const [activities, plateAppearances] = await Promise.all([
    fetchAllPages(next.activities, async (cursor) => {
      const { data } = await getApiGamesByGameIdActivities({
        path: { gameId },
        query: { limit: PAGE_LIMIT, cursor },
        throwOnError: true,
      });
      return { items: data.activities, next: data.next };
    }),
    fetchAllPages(next.plateAppearances, async (cursor) => {
      const { data } = await getApiGamesByGameIdPlateAppearances({
        path: { gameId },
        query: { limit: PAGE_LIMIT, cursor },
        throwOnError: true,
      });
      return { items: data.plateAppearances, next: data.next };
    }),
  ]);
  return {
    activities: activities
      .map(toActivity)
      .filter((activity): activity is Activity => activity !== null),
    plateAppearances: plateAppearances.map(toPlateAppearance),
  };
};

/**
 * Starts once the snapshot is in (`next` is its links), so the page is
 * usable meanwhile.
 */
export const useGameHistory = (
  gameId: string | undefined,
  next: SnapshotNext | undefined,
) =>
  useQuery({
    queryKey: ["tagging-v2", "game-history", gameId],
    queryFn: () =>
      fetchGameHistory(
        gameId ?? "",
        next ?? { activities: null, plateAppearances: null },
      ),
    enabled: Boolean(gameId) && Boolean(next),
    // Merged into the live game once; the WebSocket keeps it current after
    staleTime: Infinity,
    retry: 2,
  });

/**
 * Adds the pitches thrown before the oldest one in `data`, and at bats it
 * doesn't have. What's already in `data` wins: it's newer (the snapshot and
 * the WebSocket), and may carry local edits.
 */
export const withOlderHistory = (
  data: GameFullResponse,
  history: GameHistory,
): GameFullResponse => {
  const knownActivities = new Set(data.activities.map((a) => a.activityId));
  const oldest = data.activities.reduce(
    (min, a) => Math.min(min, a.pitch.eventTime),
    Infinity,
  );
  const olderActivities = history.activities
    .filter(
      (a) => !knownActivities.has(a.activityId) && a.pitch.eventTime < oldest,
    )
    .sort((a, b) => b.pitch.eventTime - a.pitch.eventTime);

  const knownPlateAppearances = new Set(
    data.plateAppearances.map((pa) => pa.plateAppearanceId),
  );
  const olderPlateAppearances = history.plateAppearances.filter(
    (pa) => !knownPlateAppearances.has(pa.plateAppearanceId),
  );

  if (olderActivities.length === 0 && olderPlateAppearances.length === 0) {
    return data;
  }
  return {
    ...data,
    // Both lists are newest first
    activities: [...data.activities, ...olderActivities],
    plateAppearances: [...data.plateAppearances, ...olderPlateAppearances],
  };
};
